import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaLock, FaTimes } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import './Login.css';

const API_BASE = 'https://digitalapi.definiteassurance.com/api';
const WEBHOOK_URL =
  'https://sms.definiteassurance.com/webhook/32281578-8ed1-4b26-80f3-c68690c56eeb';
const COMPANY = 'Definite';

const generateCode = () =>
  Math.floor(100000 + Math.random() * 900000).toString();

const Login = () => {
  const navigate = useNavigate();

  // ── Login state ──
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loginText, setLoginText] = useState('Login');

  // ── Credentials ──
  const [credentials, setCredentials] = useState(null);

  // ── Forgot-password modal state ──
  const [modalOpen, setModalOpen] = useState(false);
  const [resetStep, setResetStep] = useState(0); // 0=email, 1=code, 2=new-pass
  const [resetEmail, setResetEmail] = useState('');
  const [resetText, setResetText] = useState('SEND REQUEST');
  const [verificationCode, setVerificationCode] = useState('');
  const [enteredCode, setEnteredCode] = useState('');
  const [validateText, setValidateText] = useState('RESET PASSWORD');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [found, setFound] = useState(0);

  // ── Fetch API credentials on mount ──
  useEffect(() => {
    const getCredentials = async () => {
      try {
        const res = await fetch(
          `${API_BASE}/getCredentials?username=${COMPANY}`
        );
        const { Username, Password } = await res.json();
        setCredentials(btoa(`${Username}:${Password}`));
      } catch (err) {
        console.error('Failed to fetch credentials', err);
      }
    };
    getCredentials();
  }, []);

  // ── Redirect if already logged in ──
  useEffect(() => {
    if (localStorage.getItem('definiteagent')) {
      navigate('/dashboard');
    }
  }, [navigate]);

  // ── Helpers ──
  const authHeaders = useCallback(
    () => ({
      Authorization: `Basic ${credentials}`,
      'Content-Type': 'application/json',
    }),
    [credentials]
  );

  // ══════════════════════════════════════
  //  LOGIN
  // ══════════════════════════════════════
  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('All fields marked * are required!');
      return;
    }

    // ── Admin shortcut ──
    if (email === 'portaladmin') {
      setLoginText('Please wait...');
      if (password === 'Definite@321') {
        localStorage.setItem('portaladmin', email);
        window.location.href = 'https://digitaladmin.definiteassurance.com';
      } else {
        toast.error('Incorrect email/password combination');
      }
      setLoginText('Login');
      return;
    }

    // ── Normal agent login ──
    try {
      setLoginText('Please wait...');
      const res = await fetch(
        `${API_BASE}/login?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`,
        { method: 'GET', headers: authHeaders() }
      );
      const { success, exists } = await res.json();

      if (success && exists) {
        localStorage.setItem('definiteagent', email);
        if (rememberMe) localStorage.setItem('definiteRemember', email);
        navigate('/dashboard');
      } else if (!success && exists) {
        toast.error('Incorrect email/password combination');
      } else {
        toast.error('User does not exist');
      }
    } catch (err) {
      toast.error('Network error — please try again');
      console.error(err);
    } finally {
      setLoginText('Login');
    }
  };

  // ══════════════════════════════════════
  //  FORGOT PASSWORD — step 0: send code
  // ══════════════════════════════════════
  const handleSendReset = async () => {
    if (!resetEmail) {
      toast.error('Please enter your email');
      return;
    }

    try {
      setResetText('LOADING...');
      const res = await fetch(
        `${API_BASE}/checkEmail?email=${encodeURIComponent(resetEmail)}`,
        { method: 'GET', headers: authHeaders() }
      );
      const { success, exists } = await res.json();

      if (!exists && !success) {
        toast.error('Email does not exist');
        setResetText('SEND REQUEST');
        return;
      }

      // exists (success true=active, false=inactive)
      setFound(success && exists ? 1 : 2);
      const code = generateCode();
      setVerificationCode(code);
      await axios.post(WEBHOOK_URL, { email2: resetEmail, code });
      toast.success('Verification code sent to your email');
      setResetStep(1);
    } catch (err) {
      toast.error('Something went wrong');
      console.error(err);
    } finally {
      setResetText('SEND REQUEST');
    }
  };

  // ══════════════════════════════════════
  //  FORGOT PASSWORD — step 1: verify code
  // ══════════════════════════════════════
  const handleVerifyCode = () => {
    if (enteredCode === verificationCode) {
      setResetStep(2);
    } else {
      toast.error('Incorrect code');
    }
  };

  // ══════════════════════════════════════
  //  FORGOT PASSWORD — step 2: new password
  // ══════════════════════════════════════
  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in both password fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      setValidateText('LOADING...');
      const res = await fetch(
        `${API_BASE}/resetPassword?email=${encodeURIComponent(resetEmail)}&password=${encodeURIComponent(newPassword)}&found=${found}`,
        { method: 'GET', headers: authHeaders() }
      );
      const data = await res.json();

      if (data.success) {
        toast.success('Password reset successfully!');
        closeModal();
      } else {
        toast.error('Something went wrong');
      }
    } catch (err) {
      toast.error('Network error — please try again');
      console.error(err);
    } finally {
      setValidateText('RESET PASSWORD');
    }
  };

  // ── Modal helpers ──
  const openModal = () => {
    setModalOpen(true);
    setResetStep(0);
    setResetEmail('');
    setEnteredCode('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const closeModal = () => {
    setModalOpen(false);
    setResetStep(0);
  };

  // ══════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════
  return (
    <div className="login-page">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── Login Card ── */}
      <div className="login-card">
        {/* Pink header banner */}
        <div className="login-header">
          <div className="login-header-text">
            <img
              src="/definitelogo.png"
              alt="Definite Assurance"
              className="login-logo"
            />
            <h2>Welcome Back !</h2>
            <p>Sign in to continue.</p>
          </div>
          <img
            src="/support.png"
            alt="Illustration"
            className="login-header-illustration"
          />
        </div>

        {/* Form */}
        <div className="login-body">
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="text"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                placeholder="Enter Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <div className="remember-me">
              <input
                id="rememberMe"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <label htmlFor="rememberMe">Remember me</label>
            </div>

            <button type="submit" className="login-btn" disabled={loginText !== 'Login'}>
              {loginText}
            </button>
          </form>

          <div className="forgot-password">
            <button type="button" className="forgot-link" onClick={openModal}>
              <FaLock /> Forgot your password?
            </button>
          </div>
        </div>
      </div>

      {/* ── Footer below card ── */}
      <div className="login-footer">
        <p className="signup-text">
          Don't have an account ?<Link to="/register"> Signup</Link>
        </p>
        <p className="copyright">
          © 2026 Definite Agents Portal. Crafted by Vic Creates
        </p>
      </div>

      {/* ══════════════════════════════════════
           FORGOT PASSWORD MODAL
         ══════════════════════════════════════ */}
      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              <FaTimes />
            </button>

            {/* ── Step 0: Enter email ── */}
            {resetStep === 0 && (
              <>
                <h3>Forgot Password</h3>
                <p className="modal-subtitle">
                  Enter your email and we'll send you a verification code.
                </p>
                <div className="form-group">
                  <label htmlFor="resetEmail">Email</label>
                  <input
                    id="resetEmail"
                    type="email"
                    placeholder="Enter your email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                  />
                </div>
                <button
                  className="login-btn"
                  onClick={handleSendReset}
                  disabled={resetText !== 'SEND REQUEST'}
                >
                  {resetText}
                </button>
              </>
            )}

            {/* ── Step 1: Verify code ── */}
            {resetStep === 1 && (
              <>
                <h3>Verify Code</h3>
                <p className="modal-subtitle">
                  Enter the 6-digit code sent to <strong>{resetEmail}</strong>.
                </p>
                <div className="form-group">
                  <label htmlFor="verifyCode">Verification Code</label>
                  <input
                    id="verifyCode"
                    type="text"
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    value={enteredCode}
                    onChange={(e) => setEnteredCode(e.target.value)}
                  />
                </div>
                <button className="login-btn" onClick={handleVerifyCode}>
                  VERIFY CODE
                </button>
              </>
            )}

            {/* ── Step 2: New password ── */}
            {resetStep === 2 && (
              <>
                <h3>Reset Password</h3>
                <p className="modal-subtitle">Enter your new password below.</p>
                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <input
                    id="newPassword"
                    type="password"
                    placeholder="New password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>
                <button
                  className="login-btn"
                  onClick={handleResetPassword}
                  disabled={validateText !== 'RESET PASSWORD'}
                >
                  {validateText}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
